import { useAuth } from "@clerk/clerk-react";
import type { SupportTicketType } from "@pilatestime/shared-types";
import { createFileRoute } from "@tanstack/react-router";
import { Send } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCreateSupportTicket } from "@/api/supportTickets";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_auth/app/_app/help/support/")({
	component: RouteComponent,
});

interface SupportFormData {
	title: string;
	description: string;
	type: SupportTicketType | "";
}

function RouteComponent() {
	const { t } = useTranslation();
	const { getToken } = useAuth();
	const createSupportTicketMutation = useCreateSupportTicket();

	const [formData, setFormData] = useState<SupportFormData>({
		title: "",
		description: "",
		type: "",
	});
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleInputChange = (
		field: keyof SupportFormData,
		value: string | SupportTicketType,
	) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		// Validate form
		if (!formData.title.trim()) {
			toast.error(
				t("validation.required", { field: t("help.supportFieldTitle") }),
			);
			return;
		}

		if (!formData.description.trim()) {
			toast.error(
				t("validation.required", { field: t("help.supportFieldDescription") }),
			);
			return;
		}

		if (!formData.type) {
			toast.error(
				t("validation.required", { field: t("help.supportFieldType") }),
			);
			return;
		}

		setIsSubmitting(true);

		try {
			const token = await getToken();
			if (!token) {
				toast.error("Authentication required");
				return;
			}

			await createSupportTicketMutation.mutateAsync({
				token,
				data: {
					title: formData.title.trim(),
					description: formData.description.trim(),
					type: formData.type as SupportTicketType,
				},
			});

			// Reset form
			setFormData({
				title: "",
				description: "",
				type: "",
			});
		} catch (error) {
			console.error("Error submitting support request:", error);
			// Error is handled by the mutation hook
		} finally {
			setIsSubmitting(false);
		}
	};

	const isFormValid =
		formData.title.trim() && formData.description.trim() && formData.type;

	return (
		<div className="p-6 max-w-2xl mx-auto">
			{/* Support Form */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<Send className="h-5 w-5" />
						<span>{t("help.supportFormTitle")}</span>
					</CardTitle>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleSubmit} className="space-y-6">
						{/* Title Field */}
						<div className="space-y-2">
							<Label htmlFor="title" className="text-sm font-medium">
								{t("help.supportFieldTitle")}
								<span className="text-error ml-1">*</span>
							</Label>
							<Input
								id="title"
								type="text"
								value={formData.title}
								onChange={(e) => handleInputChange("title", e.target.value)}
								placeholder={t("help.supportPlaceholderTitle")}
								className="w-full"
								disabled={isSubmitting}
								maxLength={200}
							/>
							<p className="text-xs text-base-content/60">
								{t("help.supportHintTitle")}
							</p>
						</div>

						{/* Type Field */}
						<div className="space-y-2">
							<Label htmlFor="type" className="text-sm font-medium">
								{t("help.supportFieldType")}
								<span className="text-error ml-1">*</span>
							</Label>
							<Select
								value={formData.type}
								onValueChange={(value) =>
									handleInputChange("type", value as SupportTicketType)
								}
								disabled={isSubmitting}
							>
								<SelectTrigger className="w-full">
									<SelectValue placeholder={t("help.supportPlaceholderType")} />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="request">
										{t("help.supportTypeRequest")}
									</SelectItem>
									<SelectItem value="suggestion">
										{t("help.supportTypeSuggestion")}
									</SelectItem>
									<SelectItem value="complaint">
										{t("help.supportTypeComplaint")}
									</SelectItem>
								</SelectContent>
							</Select>
							<p className="text-xs text-base-content/60">
								{t("help.supportHintType")}
							</p>
						</div>

						{/* Description Field */}
						<div className="space-y-2">
							<Label htmlFor="description" className="text-sm font-medium">
								{t("help.supportFieldDescription")}
								<span className="text-error ml-1">*</span>
							</Label>
							<Textarea
								id="description"
								value={formData.description}
								onChange={(e) =>
									handleInputChange("description", e.target.value)
								}
								placeholder={t("help.supportPlaceholderDescription")}
								className="w-full min-h-32"
								disabled={isSubmitting}
								maxLength={2000}
							/>
							<p className="text-xs text-base-content/60">
								{t("help.supportHintDescription")}
							</p>
						</div>

						{/* Character Counts */}
						<div className="flex justify-between text-xs text-base-content/50">
							<span>
								{t("help.supportCharacterCountTitle")}: {formData.title.length}
								/200
							</span>
							<span>
								{t("help.supportCharacterCountDescription")}:{" "}
								{formData.description.length}/2000
							</span>
						</div>

						{/* Submit Button */}
						<div className="pt-4">
							<Button
								type="submit"
								disabled={!isFormValid || isSubmitting}
								className="w-full flex items-center justify-center space-x-2 text-primary-content"
							>
								{isSubmitting ? (
									<>
										<div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
										<span>{t("help.supportSubmitting")}</span>
									</>
								) : (
									<>
										<Send className="h-4 w-4" />
										<span>{t("help.supportSubmit")}</span>
									</>
								)}
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>
		</div>
	);
}
